import { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  IconButton,
  Drawer,
  Chip,
  Tooltip,
  Button,
  Pagination,
  Collapse,
  Alert
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CreateIcon from '@mui/icons-material/Create';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import axios from 'axios';
import { ThemeContext } from '../Context';
import GetUserAvatar from '../tool/GetUserAvatar';
import HistoryPreview from './HistoryPreview';

const DRAWER_WIDTH = 380;

// Formater la date en français
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return `Aujourd'hui à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  } else if (diffDays === 1) {
    return `Hier à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  } else if (diffDays < 7) {
    return date.toLocaleDateString('fr-FR', { weekday: 'long', hour: '2-digit', minute: '2-digit' });
  } else {
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }
};

// Grouper les entrées par jour
const groupEntriesByDay = (entries) => {
  const groups = {};
  
  entries.forEach(entry => {
    const date = new Date(entry.modifiedAt);
    const dayKey = date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
    
    if (!groups[dayKey]) {
      groups[dayKey] = [];
    }
    groups[dayKey].push(entry);
  });
  
  return groups;
};

// Icône selon le type de changement
const getChangeTypeIcon = (changeType) => {
  switch (changeType) {
    case 'create':
      return <AddIcon fontSize="small" />;
    case 'generate':
      return <AutorenewIcon fontSize="small" />;
    case 'delete':
      return <DeleteIcon fontSize="small" />;
    case 'restore':
      return <SettingsBackupRestoreIcon fontSize="small" />;
    default:
      return <CreateIcon fontSize="small" />;
  }
};

// Couleur selon le type de changement
const getChangeTypeColor = (changeType, isDarkMode) => {
  switch (changeType) {
    case 'create':
      return isDarkMode ? '#4caf50' : '#2e7d32';
    case 'generate':
      return isDarkMode ? '#2196f3' : '#1565c0';
    case 'delete':
      return isDarkMode ? '#f44336' : '#c62828';
    case 'restore':
      return isDarkMode ? '#ff9800' : '#ef6c00';
    default:
      return isDarkMode ? '#9e9e9e' : '#616161';
  }
};

// Label selon le type de changement
const getChangeTypeLabel = (changeType) => {
  switch (changeType) {
    case 'create':
      return 'Création';
    case 'generate':
      return 'Génération';
    case 'delete':
      return 'Supprimée - Cliquer pour restaurer';
    case 'restore':
      return 'Restauration';
    default:
      return 'Modification';
  }
};

// Badge selon le type de changement
const getChangeTypeBadge = (changeType, isDarkMode) => {
  if (changeType === 'delete') {
    return {
      text: 'SUPPRIMÉE',
      bgcolor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)',
      color: isDarkMode ? '#e57373' : '#c62828'
    };
  }
  return null;
};

function History({ open, onClose, reportData }) {
  const { theme } = useContext(ThemeContext);
  const isDarkMode = theme === 'dark';

  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedDays, setExpandedDays] = useState({});
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState(null);

  // Charger l'historique
  const loadHistory = useCallback(async (pageNum = 1) => {
    // Utiliser reportData._id si disponible
    const reportId = reportData?._id;
    const token = sessionStorage.getItem('authToken');
    
    if (!reportId || !token) {
      console.log('History: reportId ou token manquant', { reportId, hasToken: !!token });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('History: Chargement de l\'historique pour le rapport', reportId);
      const response = await axios.get(`/api/history/${reportId}`, {
        params: { page: pageNum, limit: 50 },
        headers: { Authorization: `Bearer ${token}` }
      });

      setEntries(response.data.entries);
      setTotalPages(response.data.totalPages);
      setPage(response.data.page);

      // Ouvrir le premier jour par défaut
      const grouped = groupEntriesByDay(response.data.entries);
      const firstDay = Object.keys(grouped)[0];
      if (firstDay) {
        setExpandedDays({ [firstDay]: true });
      }
    } catch (err) {
      console.error('Erreur lors du chargement de l\'historique:', err);
      setError('Impossible de charger l\'historique');
    } finally {
      setLoading(false);
    }
  }, [reportData?._id]);

  // Charger l'historique à l'ouverture
  useEffect(() => {
    if (open) {
      loadHistory(1);
    }
  }, [open, loadHistory]);

  // Toggle un jour
  const toggleDay = (dayKey) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayKey]: !prev[dayKey]
    }));
  };

  // Ouvrir la prévisualisation
  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
    setPreviewOpen(true);
  };

  // Fermer la prévisualisation et rafraîchir
  const handlePreviewClose = (refreshNeeded = false) => {
    setPreviewOpen(false);
    setSelectedEntry(null);
    if (refreshNeeded) {
      loadHistory(page);
    }
  };

  // Changement de page
  const handlePageChange = (event, value) => {
    loadHistory(value);
  };

  const groupedEntries = groupEntriesByDay(entries);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            bgcolor: isDarkMode ? '#1a1a2e' : '#fafafa',
            borderLeft: `1px solid ${isDarkMode ? '#333' : '#e0e0e0'}`
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${isDarkMode ? '#333' : '#e0e0e0'}`,
            bgcolor: isDarkMode ? '#16213e' : '#fff'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Historique
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Contenu */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
            </Alert>
          ) : entries.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                Aucun historique disponible
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Les modifications seront enregistrées ici
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {Object.entries(groupedEntries).map(([dayKey, dayEntries]) => (
                <Box key={dayKey}>
                  {/* Header du jour */}
                  <ListItemButton
                    onClick={() => toggleDay(dayKey)}
                    sx={{
                      py: 1,
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                          {dayKey}
                        </Typography>
                      }
                      secondary={`${dayEntries.length} modification${dayEntries.length > 1 ? 's' : ''}`}
                    />
                    {expandedDays[dayKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>

                  {/* Entrées du jour */}
                  <Collapse in={expandedDays[dayKey]} timeout="auto" unmountOnExit>
                    <List dense disablePadding>
                      {dayEntries.map((entry) => (
                        <ListItem
                          key={entry._id}
                          disablePadding
                          sx={{
                            borderLeft: `3px solid ${getChangeTypeColor(entry.changeType, isDarkMode)}`
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleEntryClick(entry)}
                            sx={{
                              py: 1.5,
                              pl: 2,
                              '&:hover': {
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
                              }
                            }}
                          >
                            <Box sx={{ width: '100%' }}>
                              {/* Ligne 1: Avatar + nom */}
                              <GetUserAvatar matricule={entry.modifiedBy} />
                              
                              {/* Ligne 2: Type + titre + heure */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                <Tooltip title={getChangeTypeLabel(entry.changeType)}>
                                  <Box sx={{ color: getChangeTypeColor(entry.changeType, isDarkMode), display: 'flex' }}>
                                    {getChangeTypeIcon(entry.changeType)}
                                  </Box>
                                </Tooltip>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ 
                                    fontWeight: 500,
                                    textDecoration: entry.changeType === 'delete' ? 'line-through' : 'none',
                                    flex: 1
                                  }}
                                >
                                  {entry.rubricTitle || 'Rubrique'}
                                </Typography>
                                {entry.changeType === 'delete' && (
                                  <Chip
                                    label="SUPPRIMÉE"
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: '0.6rem',
                                      bgcolor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)',
                                      color: isDarkMode ? '#e57373' : '#c62828',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                )}
                                <Typography variant="caption" color="text.disabled">
                                  {new Date(entry.modifiedAt).toLocaleTimeString('fr-FR', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </Typography>
                              </Box>
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                  <Divider />
                </Box>
              ))}
            </List>
          )}
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box
            sx={{
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              borderTop: `1px solid ${isDarkMode ? '#333' : '#e0e0e0'}`
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              size="small"
              color="primary"
            />
          </Box>
        )}
      </Drawer>

      {/* Modal de prévisualisation */}
      <HistoryPreview
        open={previewOpen}
        entry={selectedEntry}
        onClose={handlePreviewClose}
        reportData={reportData}
      />
    </>
  );
}

export default History;
